import { flowSpendListSchema } from "@zworks/console-contracts";
import type { FlowSpend, LedgerEntry, RunListEntry } from "@zworks/console-contracts";
import { formatCredits, formatCreditsUsd } from "./format";

/** A short line about what the entry is, for the ledger detail column and the CSV. */
export function entryDetail(entry: LedgerEntry): string {
  if (entry.note) return entry.note;
  if (entry.kind === "reserve" && entry.runId)
    return `Run ${entry.runId.slice(0, 8)} held ${formatCredits(Math.abs(entry.amount))}`;
  if (entry.kind === "release" && entry.runId) return `Run ${entry.runId.slice(0, 8)} unused hold`;
  if (entry.kind === "consume" && entry.runId) return `Run ${entry.runId.slice(0, 8)}`;
  if (entry.kind === "metered" && entry.runId)
    return `Run ${entry.runId.slice(0, 8)} (customer key)`;
  if (entry.kind === "metered") return "Customer-key usage";
  if (entry.kind === "grant") return "Welcome grant";
  if (entry.kind === "purchase") return "Credit tier";
  if (entry.kind === "refund") return "Refund";
  if (entry.runId) return `Run ${entry.runId.slice(0, 8)}`;
  return "–";
}

/** Formats a signed ledger entry amount as USD (1 credit = $0.01). */
export function formatAmountUsd(entry: LedgerEntry): string {
  if (entry.kind === "unknown") return "?";
  if (entry.kind === "metered") return "$0.00";
  const formatted = formatCreditsUsd(entry.amount);
  if (entry.kind === "purchase" || entry.kind === "grant" || entry.kind === "release") {
    return `+${formatted}`;
  }
  return `−${formatted}`;
}

/** The ledger rows as CSV, so a statement can be kept outside the console. */
export function ledgerCsv(entries: LedgerEntry[]): string {
  const header = "when,kind,detail,credits,certainty";
  const rows = entries.map((entry) =>
    [entry.createdAt, entry.kind, entryDetail(entry), String(entry.amount), entry.certainty]
      .map((cell) => `"${cell.replaceAll('"', '""')}"`)
      .join(","),
  );
  return [header, ...rows].join("\n");
}

/** One day of settled consume spend, for the Usage charts. Labels read DD/MM in UTC. */
export interface DailySpend {
  /** The UTC midnight timestamp starting the day. */
  dayStart: number;
  label: string;
  credits: number;
}

/**
 * Settle charts read the ledger, not the runs list: consume entries carry the spend the wallet
 * reconciled, including months older than the first runs page. Only `consume` entries count;
 * reserves, releases, purchases, grants, refunds, unknown, and metered entries never inflate
 * spend. Days with no spend still appear with zero, so the trend never implies gaps are missing
 * data. The window ends on today's UTC date and covers `days` days back.
 */
export function dailySpend(entries: LedgerEntry[], now: Date, days = 30): DailySpend[] {
  const safeDays = Math.max(1, Math.min(31, Math.floor(days)));
  const todayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const totals = new Array<number>(safeDays).fill(0);
  for (const entry of entries) {
    if (entry.kind !== "consume" || entry.certainty !== "confirmed") continue;
    const time = Date.parse(entry.createdAt);
    if (Number.isNaN(time)) continue;
    const index = Math.floor((time - todayStart) / 86_400_000) + (safeDays - 1);
    if (index < 0 || index >= safeDays) continue;
    totals[index] = (totals[index] ?? 0) + Math.abs(entry.amount);
  }
  return totals.map((credits, index) => {
    const dayStart = todayStart - (safeDays - 1 - index) * 86_400_000;
    const date = new Date(dayStart);
    const label = `${String(date.getUTCDate()).padStart(2, "0")}/${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
    return { dayStart, label, credits };
  });
}

/**
 * This month's settled spend grouped by flow, from the runs list. Flows have no tables yet, so
 * the name is the frozen flow version on the run; a null version reads as unlabelled rather
 * than guessed. Unknown-certainty spend stays visible in the ledger, never in this table.
 */
export function spendByFlow(runs: RunListEntry[], now: Date): FlowSpend[] {
  const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  const totals = new Map<string | null, number>();
  for (const run of runs) {
    if (run.creditSpend === null) continue;
    if (run.creditCertainty === "unknown") continue;
    if (new Date(run.startedAt).getTime() < monthStart) continue;
    totals.set(run.flowVersion, (totals.get(run.flowVersion) ?? 0) + run.creditSpend);
  }
  return flowSpendListSchema.parse(
    [...totals.entries()]
      .map(([flowVersion, credits]) => ({ flowVersion, credits }))
      .sort((left, right) => right.credits - left.credits),
  );
}
